import Vue from "vue";
import VueRouter from "vue-router";
import middlewarePipeline from "@/middlewarePipeline";
import auth from "@/middleware/auth";
import guest from "@/middleware/guest";
import admin from "@/middleware/admin";

// importing components
import Login from "@/components/Login";
import Register from "@/components/Register";
import CatalyticConverterList from "@/components/catalyticConverters/CatalyticConverterList";
import SearchCatalyticConverters from "@/components/catalyticConverters/SearchCatalyticConverters";
import ShowCatalyticConverter from "@/components/catalyticConverters/ShowCatalyticConverter";
import AddEditCatalyticConverter from "@/components/catalyticConverters/AddEditCatalyticConverter";
import AddEditVehicle from "@/components/vehicles/AddEditVehicle";
import UserList from "@/components/Users/UserList";
import User from "@/components/Users/User";
import AddEditUser from "@/components/Users/AddEditUser";
import UpdateUserPassword from "@/components/Users/UpdateUserPassword";
import ConfirmCheckInSheet from "@/components/checkInSheets/ConfirmCheckInSheet";
import CreateCheckInSheet from "@/components/checkInSheets/CreateCheckInSheet";
import CheckInSheet from "@/components/checkInSheets/CheckInSheet";
import CheckInSheetList from "@/components/checkInSheets/CheckInSheetList";
import UserLogs from "@/components/UserLogs"
import VehiclesList from "@/components/vehicles/VehiclesList";
import NotFound from "@/components/NotFound";
import store from "./store";

Vue.use(VueRouter);

// Navigation duplicated error fix https://stackoverflow.com/a/62331463
const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => {
    if (err.name !== "NavigationDuplicated") throw err;
  });
};

// https://laravelvuespa.com/middleware/middleware-overview
const routes = [
  {
    path: "/",
    name: "search",
    component: SearchCatalyticConverters,
    meta: { middleware: [auth] },
  },
  {
    path: "/login",
    name: "login",
    component: Login,
    //only guests, unauthed users can see this page
    meta: { middleware: [guest] },
  },
  {
    path: "/register",
    name: "register",
    component: Register,
    meta: { middleware: [auth, admin] },
  },
  {
    path: "/catalytic-converters",
    name: "catalytic-converters",
    component: CatalyticConverterList,
    meta: { middleware: [auth, admin] },
  },
  {
    path: "/catalytic-converters/add",
    component: AddEditCatalyticConverter,
    name:"add-converter",
    meta: { middleware: [auth, admin] },
  },
  {
    path: "/catalytic-converters/:id/edit",
    component: AddEditCatalyticConverter,
    name:"edit-converter",
    props: (route) => ({ isEdit: true, id: route.params.id }),
    meta: { middleware: [auth, admin] },
  },
  {
    path: "/catalytic-converters/:id",
    name:"single-converter",
    component: ShowCatalyticConverter,
    props: true,
    meta: { middleware: [auth] },
  },
  {
    path: "/user-logs/:id?",
    name: "user-logs",
    component: UserLogs,
    props: true,
    meta: { middleware: [auth, admin] },
  },
  {
    path: "/users",
    name: "users",
    component: UserList,
    meta: { middleware: [auth, admin] },
  },
  {
    path: "/users/add",
    name: "user-add",
    component: AddEditUser,
    meta: { middleware: [auth, admin] },
  },
  {
    path: "/users/:id",
    name: "single-user",
    component: User,
    meta: { middleware: [auth, admin] },
  },
  {
    path: "/user/:id/edit",
    name: "user-edit",
    component: AddEditUser,
    props: () => ({ isEdit: true }),
    meta: { middleware: [auth, admin] },
  },
  {
    path: "/users/:id/update-password",
    name: "change-user-password",
    component: UpdateUserPassword,
    props:true,
    meta: { middleware: [auth, admin] },
  },
  {
    path: "/check-in-sheets",
    name: "check-in-sheets",
    component: CheckInSheetList,
    meta: { middleware: [auth] },
  },
  {
    path: "/check-in-sheets/add",
    name: "check-in-sheet-add",
    component: CreateCheckInSheet,
    meta: { middleware: [auth, admin] },
  },
  {
    path: "/check-in-sheets/:id",
    name: "single-check-in-sheet",
    component: CheckInSheet,
    props: (route) => ({ id: route.params.id }),
    meta: { middleware: [auth] },
  },
  {
    path: "/check-in-sheets/:id/confirm",
    name: "check-in-sheet-confirm",
    component: ConfirmCheckInSheet,
    props: (route) => ({ id: route.params.id }),
    meta: { middleware: [auth] },
  },
  {
    path: "/vehicles",
    name: "vehicles",
    component: VehiclesList,
    meta: { middleware: [auth, admin] },
  },
  {
    path: "/vehicles/add",
    name: "add-vehicle",
    component: AddEditVehicle,
    meta: { middleware: [auth, admin] },
  },
  {
    path: "/not-found",
    mame: "not-found",
    component: NotFound,
  },
  {
    path: "/vehicles/:id/edit",
    name: "edit-vehicle",
    component: AddEditVehicle,
    props: (route) => ({ isEdit: true, id: route.params.id }),
    meta: { middleware: [auth, admin] },
  },
];

const router = new VueRouter({
  mode: "history",
  linkExactActiveClass: "active",
  routes,
});

// building a middleware pipeline i.e. middleware design pattern in vue router
router.beforeEach(async (to, from, next) => {
  //array of middlewares
  const middleware = to.meta.middleware;
  //build context object to pass where we are going and
  const context = { to, from, next, store };

  //if there isn't a middleware on route, then just call next() | guard condition
  if (!middleware) {
    return next();
  }
  // run the first middleware in array
  // next in this context is our own recursive function that goes through the middleware functions in the routes
  middleware[0]({
    ...context,
    next: middlewarePipeline(context, middleware, 1),
  });
});

export default router;
